import { useState, useEffect } from "react";
import { useLazyQuery } from "@apollo/client";
import { useDebouncedCallback } from "use-debounce";
import { FIND_RELEVANT_EXTERNAL_MATTERS_QUERY } from "~/graphql/query";
import { MatterFilter } from "~/design-system/data-types/matter-filter";

const DEFAULT_DISTANCE_THRESHOLD = 0.85;
const RETRY_DISTANCE_THRESHOLD = 1.0;

export type UseRelevantExternalMatterSearchOptions = {
  additionalSearchText?: string;
  autoRetry?: boolean;
  unlinkedOnly?: boolean;
};

const transformMatterFilter = (filter: MatterFilter) => {
  return {
    title: filter.title,
    organisationalUnitId: filter.orgUnit,
    clientId: filter.client,
    updatedAtFrom: filter.updatedAt?.from,
    updatedAtTo: filter.updatedAt?.to,
    currencyId: filter.currencyId,
    feeEarnerId: filter.feeEarner,
  };
};

export const useRelevantExternalMatterSearch = (
  searchText: string,
  clientName: string,
  matterFilter: MatterFilter,
  options: UseRelevantExternalMatterSearchOptions = {}
) => {
  const { additionalSearchText = null, autoRetry = true, unlinkedOnly = true } = options;
  const [distanceThreshold, setDistanceThreshold] = useState(DEFAULT_DISTANCE_THRESHOLD);

  const [
    fetchExternalMattersQuery,
    {
      data: externalMattersData,
      loading: externalMattersLoading,
      error: externalMattersError,
      refetch: refetchExternalMatters,
    },
  ] = useLazyQuery(FIND_RELEVANT_EXTERNAL_MATTERS_QUERY);

  const debouncedSearch = useDebouncedCallback(() => {
    if (!searchText.trim()) return;

    fetchExternalMattersQuery({
      variables: {
        text: searchText,
        clientName,
        filter: transformMatterFilter(matterFilter),
        fetchAccruedFees: true,
        unlinkedOnly,
        distanceThreshold,
        additionalSearchText: additionalSearchText?.trim() || null,
      },
    });
  }, 300);

  // Run the query whenever search text, filter, or distance threshold changes.
  useEffect(() => {
    if (searchText.trim()) {
      debouncedSearch();
    }
  }, [searchText, matterFilter, additionalSearchText, distanceThreshold, debouncedSearch]);

  // Auto-retry logic: if autoRetry is enabled and the result is empty, then expand the distance threshold.
  useEffect(() => {
    if (!autoRetry) return;

    if (
      externalMattersData &&
      externalMattersData.findRelevantExternalMatters &&
      externalMattersData.findRelevantExternalMatters.externalMatters.length === 0 &&
      distanceThreshold === DEFAULT_DISTANCE_THRESHOLD
    ) {
      setDistanceThreshold(RETRY_DISTANCE_THRESHOLD);
    }
  }, [autoRetry, externalMattersData, distanceThreshold]);

  return {
    distanceThreshold,
    externalMattersData,
    externalMattersLoading,
    externalMattersError,
    refetch: refetchExternalMatters,
    triggerSearch: debouncedSearch,
  };
};
